import MetaApiService from 'core/api/meta/meta-api.service';
import { useEffect } from 'react';
import Toggle from '@atlaskit/toggle';
import { useFlags } from '@atlaskit/flag';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { getRouteOptionNameFromKey } from 'core/constants/route-options';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import EditUnsuccessfulDeliveryReasonsDialog from './edit-unsuccessful-delivery-reasons-dialog';
import PageHeader from 'shared/components/page-header/page-header';
import { useSharedData } from 'core/providers/SharedDataProvider';

const PharmacySettings = () => {
  const flags = useFlags();
  const dialog = useDialog();
  const { deliveryMeta: meta } = useSharedData();

  useEffect(() => {
    if (!meta?.unsuccessfulDeliveryReasons) {
      MetaApiService.update('deliveries', {
        unsuccessfulDeliveryReasons: [
          {
            key: 'pna',
            name: 'Patient - not available',
          },
          {
            key: 'pnh',
            name: 'Patient - not home',
          },
          {
            key: 'prd',
            name: 'Patient - refused delivery',
          },
          {
            key: 'dvi',
            name: 'D - Hive - vehicle issue',
          },
          {
            key: 'dtc',
            name: 'D - Hive - time constraint',
          },

          {
            key: 'danl',
            name: 'D - Hive - address not located',
          },
          {
            key: 'dwa',
            name: 'Pride - wrong address',
          },
          {
            key: 'dwp',
            name: 'Pride - wrong package',
          },
          {
            key: 'dmp',
            name: 'Pride - missing package',
          },
          {
            key: 'other',
            name: 'Other',
          },
        ],
      });
    }
  }, [meta?.unsuccessfulDeliveryReasons]);

  const handleSettingsChange = async (key: string, enabled: boolean) => {
    try {
      switch (key) {
        case 'sendSMSMessages':
          await MetaApiService.update('deliveries', { sendSMSMessages: enabled });
          break;
        default:
          const newValue = meta?.possibleRoutes.map((route) =>
            route.key !== key
              ? route
              : {
                  ...route,
                  enabled,
                }
          );

          const locationStatusValue = meta?.locationStatus?.map((status) =>
            status.key !== key ? status : { ...status, active: enabled }
          );

          await MetaApiService.update('deliveries', {
            possibleRoutes: newValue,
            locationStatus: locationStatusValue,
          });
      }
      showSuccessFlag('Update successful', 'The setting was successfully updated.', flags);
    } catch (error) {
      console.log(error);
      showErrorFlag('An error occurred', 'Settings could not be updated, please try again.', flags);
    }
  };

  return (
    <>
      <PageHeader title='Pharmacy settings' />
      <div className='grid grid-cols-1 gap-6 mt-6 h-max'>
        <div className='bg-white w-full rounded-md shadow-md h-fit'>
          <div className='flex items-center p-4 border-b'>
            <p className='headline-05'>Notifications</p>
          </div>
          <div className='border-b last:border-0 p-4 flex justify-between items-center'>
            <p>SMS Notifications</p>
            <Toggle
              size='large'
              onChange={() => handleSettingsChange('sendSMSMessages', !meta?.sendSMSMessages)}
              isChecked={meta?.sendSMSMessages}
            />
          </div>
        </div>
        <div className='bg-white w-full rounded-md shadow-md h-fit'>
          <div className='flex items-center p-4 border-b'>
            <p className='headline-05'>Routes</p>
          </div>
          {meta?.possibleRoutes.map((route) => (
            <div key={route.key} className='border-b last:border-0 p-4 flex justify-between items-center'>
              <p>{getRouteOptionNameFromKey(route.key)}</p>
              <Toggle
                size='large'
                onChange={() => handleSettingsChange(route.key, !route.enabled)}
                isChecked={route.enabled}
              />
            </div>
          ))}
        </div>
        <div className='bg-white w-full rounded-md shadow-md h-fit'>
          <div className='flex items-center p-4 border-b justify-between'>
            <p className='headline-05'>Unsuccessful delivery reasons</p>
            <SharedButton
              onClick={() =>
                dialog?.openDialog(
                  <EditUnsuccessfulDeliveryReasonsDialog reasons={meta?.unsuccessfulDeliveryReasons ?? []} />
                )
              }
              type='button'
              appearance='primary'
              label='Edit'
            />
          </div>
          {meta?.unsuccessfulDeliveryReasons?.map((reason) => (
            <div key={reason.key} className='border-b last:border-0 p-4'>
              <p>{reason.name}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PharmacySettings;
